import {
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent
} from '@dnd-kit/core';
import { addMinutes } from 'date-fns';
import { useState } from 'react';

import type { CalendarEvent } from '../types';
import { addMinutesInTimeZone, startOfDayInTimeZone, minutesFromDayStart } from '../utils/dateGrid';
import { pxToMinutes, snapMinutes } from '../utils/grid';

const MIN_DURATION_MINUTES = 15;

export interface CalendarDraggableData {
  event: CalendarEvent;
  resizeEdge?: 'start' | 'end';
}

export interface CalendarDroppableData {
  day: Date;
  /** True for droppables (e.g. MonthCell) with no intra-day time axis. */
  wholeDay?: boolean;
}

interface UseCalendarDragOptions {
  timeZone: string;
  pxPerMinute: number;
  snapToMinutes: number;
  onEventChange?: (event: CalendarEvent, newStart: Date, newEnd: Date) => void;
}

export function useCalendarDrag({
  timeZone,
  pxPerMinute,
  snapToMinutes,
  onEventChange
}: UseCalendarDragOptions) {
  const [activeEvent, setActiveEvent] = useState<CalendarEvent | null>(null);
  const [activeSize, setActiveSize] = useState<{ width: number; height: number } | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function handleDragStart(dragEvent: DragStartEvent) {
    const data = dragEvent.active.data.current as CalendarDraggableData | undefined;
    setActiveEvent(data?.event ?? null);
    const initialRect = dragEvent.active.rect.current.initial;
    setActiveSize(initialRect ? { width: initialRect.width, height: initialRect.height } : null);
  }

  function handleDragCancel() {
    setActiveEvent(null);
    setActiveSize(null);
  }

  function handleDragEnd(dragEvent: DragEndEvent) {
    setActiveEvent(null);
    setActiveSize(null);
    const draggable = dragEvent.active.data.current as CalendarDraggableData | undefined;
    if (!draggable || !onEventChange) return;

    const { event, resizeEdge } = draggable;
    const minutesDelta = snapMinutes(pxToMinutes(dragEvent.delta.y, pxPerMinute), snapToMinutes);

    if (resizeEdge === 'start') {
      const maxStart = new Date(event.end.getTime() - MIN_DURATION_MINUTES * 60_000);
      const newStart = addMinutes(event.start, minutesDelta);
      onEventChange(event, newStart > maxStart ? maxStart : newStart, event.end);
      return;
    }
    if (resizeEdge === 'end') {
      const minEnd = new Date(event.start.getTime() + MIN_DURATION_MINUTES * 60_000);
      const newEnd = addMinutes(event.end, minutesDelta);
      onEventChange(event, event.start, newEnd < minEnd ? minEnd : newEnd);
      return;
    }

    const droppable = dragEvent.over?.data.current as CalendarDroppableData | undefined;
    const duration = event.end.getTime() - event.start.getTime();

    if (!droppable) {
      const newStart = addMinutes(event.start, minutesDelta);
      onEventChange(event, newStart, new Date(newStart.getTime() + duration));
      return;
    }

    const originalDayStart = startOfDayInTimeZone(event.start, timeZone);
    const timeOfDayMinutes = minutesFromDayStart(event.start, originalDayStart, timeZone);
    const offsetMinutes = droppable.wholeDay ? timeOfDayMinutes : timeOfDayMinutes + minutesDelta;
    const newStart = addMinutesInTimeZone(droppable.day, offsetMinutes, timeZone);
    onEventChange(event, newStart, new Date(newStart.getTime() + duration));
  }

  return { sensors, activeEvent, activeSize, handleDragStart, handleDragEnd, handleDragCancel };
}
